import { apiClient } from '@/api/client';
import { computeFormDataHash } from '@/utils/data/formDataHash';
import {
  buildCargoHashMapWithIndex,
  buildCtuHashMap,
  hashMapToRecord,
} from '@/utils/data/fieldHashing';
import type {
  ExportResponse,
  TransactionResponse,
  TransactionWithArtifactsResponse,
  CuratedArtifactResponse,
  DeltaCheckErrorsRequest,
  ErrorReport,
  ErrorReviewResponse,
  ValidationFormData,
  ValidationMetadata,
  WebhookPayload,
} from '@/api/types';

// API Functions
export async function getTransactionFull(
  transactionId: string
): Promise<TransactionWithArtifactsResponse> {
  const { data } = await apiClient.get(`/api/v1/transactions/${transactionId}`, {
    params: { include: 'artifacts' },
  });
  return data;
}

export async function getExportPreview(transactionId: string): Promise<WebhookPayload> {
  const { data } = await apiClient.get(`/api/v1/transactions/${transactionId}/export/preview`);
  return data;
}

export async function getTransactionPdf(transactionId: string): Promise<Blob> {
  const { data } = await apiClient.get(`/api/v1/transactions/${transactionId}/pdf`, {
    responseType: 'blob',
  });
  return data;
}

export async function getPageImage(imageUrl: string): Promise<Blob> {
  const { data } = await apiClient.get(imageUrl, {
    responseType: 'blob',
  });
  return data;
}

export async function getDebugArtifact(
  transactionId: string,
  artifactName: string
): Promise<unknown> {
  const { data } = await apiClient.get(
    `/api/v1/transactions/${transactionId}/artifacts/${artifactName}`
  );
  return data;
}

export async function getTransactionArtifactText(
  transactionId: string,
  artifactName: string
): Promise<string> {
  const { data } = await apiClient.get(
    `/api/v1/transactions/${transactionId}/artifacts/${artifactName}`,
    { responseType: 'text', transformResponse: (v) => v }
  );
  return data as string;
}

export async function saveCuratedData(
  transactionId: string,
  curatedData: CuratedArtifactResponse
): Promise<TransactionResponse> {
  const { data } = await apiClient.put(`/api/v1/transactions/${transactionId}/curation`, {
    curatedData,
  });
  return data;
}

/** Persists the curated data and queues the curated document for webhook delivery. */
export async function exportDocument(
  transactionId: string,
  curatedData: CuratedArtifactResponse
): Promise<ExportResponse> {
  const { data } = await apiClient.post(`/api/v1/transactions/${transactionId}/export`, {
    curatedData,
  });
  return data;
}

export async function getErrorReview(transactionId: string): Promise<ErrorReviewResponse> {
  const { data } = await apiClient.get(`/api/v1/transactions/${transactionId}/error-review`);
  return data;
}

export async function checkErrorsDelta(
  transactionId: string,
  request: DeltaCheckErrorsRequest,
  signal?: AbortSignal
): Promise<ErrorReport> {
  const { data } = await apiClient.post(
    `/api/v1/transactions/${transactionId}/error-checks`,
    request,
    { signal }
  );
  return data;
}

/**
 * Payload-free initial error check — backend computes from its stored curated form data.
 * Curated-only: other plans backfill fields client-side the artifact lacks (see
 * hydrateTransaction). Resolves undefined on failure — an enhancement, not a blocker.
 */
export async function fetchInitialErrorReport(
  transactionId: string | undefined
): Promise<ErrorReport | undefined> {
  if (!transactionId) return undefined;
  try {
    const { data } = await apiClient.get(
      `/api/v1/transactions/${transactionId}/error-checks/initial`
    );
    return data;
  } catch (err) {
    console.error('[fetchInitialErrorReport]', err);
    return undefined;
  }
}

/**
 * Full-upload error check for client-only form state: freshAuto validates substances
 * after load, so the report must reflect post-validation goods the backend hasn't stored.
 */
export async function fetchPostValidationErrorReport(
  transactionId: string | undefined,
  formData: ValidationFormData
): Promise<ErrorReport | undefined> {
  if (!transactionId) return undefined;
  try {
    return await checkErrorsDelta(transactionId, buildFullDeltaRequest(formData));
  } catch (err) {
    console.error('[fetchPostValidationErrorReport]', err);
    return undefined;
  }
}

/** Build a "cold cache" delta request — every cargo + CTU as changed. */
export function buildFullDeltaRequest(formData: ValidationFormData): DeltaCheckErrorsRequest {
  const { hashes: cargoHashes } = buildCargoHashMapWithIndex(formData.dangerousGoods);
  const ctuHashes = buildCtuHashMap(formData.cargoTransportUnits);
  return {
    changedCargo: formData.dangerousGoods,
    changedCtus: formData.cargoTransportUnits,
    cargoHashes: hashMapToRecord(cargoHashes),
    ctuHashes: hashMapToRecord(ctuHashes),
    totalCargoItems: formData.dangerousGoods.length,
  };
}

export function buildSavePayload(
  transactionId: string,
  formData: ValidationFormData,
  metadata: ValidationMetadata
): {
  mutationVars: {
    transactionId: string;
    curatedData: CuratedArtifactResponse;
  };
  formHash: string;
} {
  const formHash = computeFormDataHash(formData);

  // validation_results omitted — backend derives them from the substance_validation artifact.
  return {
    mutationVars: {
      transactionId,
      curatedData: {
        form_data: formData,
        metadata,
      },
    },
    formHash,
  };
}
